import type { AuthStorage } from '@delta/auth';
import axios, { type AxiosInstance } from 'axios';

export interface ApiClientOptions<TUser = unknown> {
  baseURL?: string;
  timeout?: number;
  authStorage?: AuthStorage<TUser>;
  unauthorizedStatuses?: readonly number[];
  onUnauthorized?: () => void;
}

export function createApiClient<TUser = unknown>({
  baseURL = '/api',
  timeout = 10_000,
  authStorage,
  unauthorizedStatuses = [401],
  onUnauthorized,
}: ApiClientOptions<TUser> = {}): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout,
    headers: { Accept: 'application/json' },
  });

  if (authStorage) {
    client.interceptors.request.use((config) => {
      const token = authStorage.getToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });
  }

  client.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (
        axios.isAxiosError(error)
        && unauthorizedStatuses.includes(error.response?.status ?? 0)
      ) {
        authStorage?.clear();
        onUnauthorized?.();
      }
      return Promise.reject(error);
    },
  );

  return client;
}

export { axios };
